import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Users, Rocket, ChevronRight } from 'lucide-react';

const WhyUs = () => {
  // Animation variants for staggered entrance
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  // Define university brand colors
  const primaryColor = '#0e4d38';
  const lightColor = '#f3f7f5';
  const accentColor = '#e2b13c';

  const features = [
    {
      icon: <BookOpen size={48} strokeWidth={1.5} />,
      title: "Top Placements & Global Opportunities",
      description: "Our world-class faculty and cutting-edge programs empower students to excel in their fields, with a curriculum designed to foster critical thinking and global perspectives."
    },
    {
      icon: <Users size={48} strokeWidth={1.5} />,
      title: "State-of-the-art Infrastructure",
      description: "Join a diverse, inclusive community where lifelong friendships are formed, and students thrive in a supportive environment filled with clubs, events, and mentorship."
    },
    {
      icon: <Rocket size={48} strokeWidth={1.5} />,
      title: "International Collaborations",
      description: "Engage in groundbreaking research with state-of-the-art facilities, collaborating on projects that address global challenges and shape the future."
    }
  ];

  return (
    <section className="w-full py-20 bg-gradient-to-b from-gray-50 to-gray-100 font-sans relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-repeat" 
             style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230e4d38' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}}></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Title */}
        <motion.div
          variants={titleVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 rounded-full bg-green-100 text-green-800 font-medium text-sm mb-4">
            EXCELLENCE & INNOVATION
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800">
            Why Choose <span className="text-green-800">Maya Devi University</span>?
          </h2>
          <div className="w-20 h-1 bg-green-700 mx-auto mb-6"></div>
          <p className="mt-4 text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Discover a world of opportunities where excellence meets innovation and tradition meets the future.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className="bg-white rounded-xl shadow-md p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 group"
            >
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-6 mx-auto group-hover:bg-green-700 group-hover:text-white text-green-700 transition-colors duration-300">
                {feature.icon}
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-800 text-center group-hover:text-green-700 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-center leading-relaxed">
                {feature.description}
              </p>
              {/* <div className="mt-6 text-center">
                <a href="#" className="inline-flex items-center text-green-700 font-medium hover:text-green-900 transition-colors">
                  Learn more <ChevronRight size={16} className="ml-1" />
                </a>
              </div> */}
            </motion.div>
          ))}
        </motion.div>

        {/* Statistics Section */}
        {/* <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-20 bg-green-800 rounded-2xl p-8 md:p-12 shadow-xl"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="p-4">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">94%</h3>
              <p className="text-green-100">Placement Rate</p>
            </div>
            <div className="p-4">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">120+</h3>
              <p className="text-green-100">Industry Partners</p>
            </div>
            <div className="p-4">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">50+</h3>
              <p className="text-green-100">Research Centers</p>
            </div>
            <div className="p-4">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">30+</h3>
              <p className="text-green-100">Global Universities</p>
            </div>
          </div>
        </motion.div> */}

        {/* Call to Action */}
        {/* <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <a
            href="/admissions"
            className="inline-flex items-center px-8 py-4 rounded-lg bg-green-700 text-white font-semibold text-lg transition-all duration-300 hover:bg-green-800 hover:shadow-lg transform hover:-translate-y-1"
          >
            Explore Our Programs <ChevronRight size={20} className="ml-2" />
          </a>
        </motion.div> */}
      </div>
    </section>
  );
};

export default WhyUs;